import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from '@shared/authContext';
import { ClientModalProvider } from '@shared/clientModalContext';
import { canAccessAdvancedSettings } from '@shared/permissions';
import Layout from './components/Layout';
import Login from './pages/Login';
import RegularServiceDashboard from './pages/RegularServiceDashboard';
import PriorityServiceDashboard from './pages/PriorityServiceDashboard';
import TechAssignments from './pages/TechAssignments';
import Users from './pages/Users';
import Clients from './pages/Clients';
import ServiceCalls from './pages/ServiceCalls';
import ServiceHistoryOverview from './pages/ServiceHistoryOverview';
import MonthlyRoster from './pages/MonthlyRoster';
import Inventory from './pages/Inventory';
import PartsNeeded from './pages/PartsNeeded';
import Profile from './pages/Profile';
import AdvancedSettings from './pages/AdvancedSettings';
import TechMetrics from './pages/TechMetrics';
import LoadingSpinner from './components/LoadingSpinner';

function App() {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Ensure only admin users can access this app
  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-6">
            You don't have permission to access the admin dashboard.
          </p>
          <button
            onClick={() => window.location.href = `${window.location.origin}/tech`}
            className="btn btn-primary"
          >
            Go to Technician App
          </button>
        </div>
      </div>
    );
  }

  const canViewAdvancedSettings = canAccessAdvancedSettings(user);

  return (
    <>
      <Toaster position="top-right" />
      <ClientModalProvider>
      <Layout>
        <Routes>
          <Route path="/" element={<Navigate to="/regular-service" replace />} />
          <Route path="/regular-service" element={<RegularServiceDashboard />} />
          <Route path="/priority-service" element={<PriorityServiceDashboard />} />
          <Route path="/tech-assignments" element={<TechAssignments />} />
          <Route path="/users" element={<Users />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/service-history" element={<ServiceHistoryOverview />} />
          <Route path="/monthly-roster" element={<MonthlyRoster />} />
          <Route path="/service-calls" element={<ServiceCalls />} />
          <Route path="/tech-metrics" element={<TechMetrics />} />
          <Route path="/inventory" element={<Inventory />} />
          <Route path="/parts-needed" element={<PartsNeeded />} />
          <Route
            path="/advanced-settings"
            element={
              canViewAdvancedSettings ? (
                <AdvancedSettings />
              ) : (
                <Navigate to="/regular-service" replace />
              )
            }
          />
          <Route path="/profile" element={<Profile />} />
          <Route path="*" element={<Navigate to="/regular-service" replace />} />
        </Routes>
      </Layout>
      </ClientModalProvider>
    </>
  );
}

export default App;
